import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import notificacionService from '../services/notificacionService';

export const RUTA_SEGUIMIENTO = '/herramientas/seguimiento-expedientes';

// Notificaciones de Seguimiento de Expedientes ANSES (origen === 'ANSES'). Extraído de
// components/NotificacionCampanaANSES.js para compartir fuente con la campana unificada mobile.
//
// `habilitado` (default true) existe porque las reglas de Hooks no permiten llamar a este hook
// solo cuando el usuario tiene el permiso 'herramientas.expedientes' — a diferencia del
// componente desktop, que directamente no se monta sin ese permiso. Con `habilitado=false` el
// hook no hace ningún fetch ni poll, mismo efecto neto que no montarlo.
export default function useNotificacionesAnses(habilitado = true) {
    const [notificaciones, setNotificaciones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [marcandoTodas, setMarcandoTodas] = useState(false);
    const navigate = useNavigate();

    const cargarNotificaciones = useCallback(async () => {
        try {
            const [pendientesRes, ultimasRes] = await Promise.all([
                notificacionService.getPendientes(),
                notificacionService.getUltimas()
            ]);

            const rawPendientes = pendientesRes?.data ?? pendientesRes;
            const rawUltimas = ultimasRes?.data ?? ultimasRes;
            const pendientes = Array.isArray(rawPendientes) ? rawPendientes : [];
            const ultimas = Array.isArray(rawUltimas) ? rawUltimas : [];

            const notificacionesMap = new Map();
            pendientes.forEach(n => notificacionesMap.set(n.id, n));
            ultimas.forEach(n => {
                if (!notificacionesMap.has(n.id)) notificacionesMap.set(n.id, n);
            });

            const deAnses = Array.from(notificacionesMap.values())
                .filter(n => n.origen === 'ANSES')
                .sort((a, b) => new Date(b.fechaProgramada) - new Date(a.fechaProgramada));

            setNotificaciones(deAnses);
        } catch (err) {
            console.error('Error al obtener notificaciones de ANSES:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    const marcarLeida = useCallback((id) => {
        setNotificaciones(prev => prev.map(n => n.id === id ? { ...n, leido: true } : n));
        notificacionService.marcarComoLeidaUsuario(id).catch(err =>
            console.error('Error al marcar notificación de ANSES como leída:', err)
        );
    }, []);

    // { onNavegar } se llama justo antes de navegar, mismo orden que tenía el componente original.
    const abrirNotificacion = useCallback((notif, { onNavegar } = {}) => {
        if (!notif.id) return;
        if (!notif.leido) marcarLeida(notif.id);
        onNavegar?.();
        // Si la notificación trae el CUIL del cliente, va directo a SU detalle; si no (ej. avisos
        // de sistema como "cola trabada"), cae a la página general de Seguimiento de Expedientes.
        if (notif.cuilClienteAnses) {
            navigate(`${RUTA_SEGUIMIENTO}/cliente/${encodeURIComponent(notif.cuilClienteAnses)}`);
        } else {
            navigate(RUTA_SEGUIMIENTO);
        }
    }, [navigate, marcarLeida]);

    const marcarTodasLeidas = useCallback(async () => {
        if (marcandoTodas || !notificaciones.some(n => !n.leido)) return;
        setMarcandoTodas(true);
        setNotificaciones(prev => prev.map(n => ({ ...n, leido: true })));
        try {
            await notificacionService.marcarTodasComoLeidas('ANSES');
        } catch (err) {
            console.error('Error al marcar todas las notificaciones de ANSES como leídas:', err);
            await cargarNotificaciones();
        } finally {
            setMarcandoTodas(false);
        }
    }, [marcandoTodas, notificaciones, cargarNotificaciones]);

    const descartar = useCallback(async (idNotificacion) => {
        if (!idNotificacion) return;
        try {
            await notificacionService.marcarComoLeidaUsuario(idNotificacion);
            await cargarNotificaciones();
        } catch (err) {
            console.error('Error al descartar notificación de ANSES:', err);
        }
    }, [cargarNotificaciones]);

    useEffect(() => {
        if (!habilitado) return;
        cargarNotificaciones();
        const intervalo = setInterval(cargarNotificaciones, 60000);
        window.recargarCampanaNotificacionesANSES = cargarNotificaciones;
        return () => {
            clearInterval(intervalo);
            delete window.recargarCampanaNotificacionesANSES;
        };
    }, [habilitado, cargarNotificaciones]);

    const nuevasCount = notificaciones.filter(n => n?.leido === false).length;

    return {
        notificaciones,
        loading,
        nuevasCount,
        hayNoLeidas: nuevasCount > 0,
        marcandoTodas,
        marcarLeida,
        abrirNotificacion,
        marcarTodasLeidas,
        descartar,
        recargar: cargarNotificaciones,
    };
}
